
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { StatusPill } from "@/components/ui/StatusPill";
import {
  getShopifyStatus,
  saveShopifyConnection,
  testShopifyConnection,
} from "@/lib/api/endpoints";

type ShopifyTestState =
  | { kind: "idle" }
  | { kind: "testing" }
  | { kind: "ok"; shopName: string }
  | { kind: "error"; message: string };

export function ShopifyCard() {
  const [shopDomain, setShopDomain] = useState("");
  const [accessToken, setAccessToken] = useState("");
  // When connected, the token is never sent back to the client — we show a
  // masked placeholder and only PUT when the user types a fresh one.
  const [tokenMasked, setTokenMasked] = useState(false);

  const [connected, setConnected] = useState(false);

  const [test, setTest] = useState<ShopifyTestState>({ kind: "idle" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);


  // Load current status on mount.
  useEffect(() => {
    let live = true;
    getShopifyStatus()
      .then((status) => {
        if (!live || !status) return;
        if (status.connected) {
          setConnected(true);
          setShopDomain(status.shopDomain ?? "");
          // Token is stored server-side only; show a masked placeholder.
          setAccessToken("••••••••••••••••");
          setTokenMasked(true);
        }
      })
      .catch(() => {
        /* status is advisory; ignore load failures */
      });
    return () => {
      live = false;
    };
  }, []);

  const tokenDirty = !tokenMasked && accessToken.length > 0;
  const dirty = tokenDirty || (!connected && shopDomain.trim().length > 0);

  const onReplaceToken = () => {
    setAccessToken("");
    setTokenMasked(false);
  };

  const runTest = useCallback(async () => {
    const domain = shopDomain.trim();
    if (!domain || tokenMasked || accessToken.length === 0) {
      setTest({
        kind: "error",
        message: tokenMasked
          ? "Click Replace to enter a token, then test"
          : "Shop domain + access token required",
      });
      return;
    }
    setTest({ kind: "testing" });
    try {
      const data = await testShopifyConnection({ shopDomain: domain, accessToken });
      if (data.ok && data.shopName) {
        setTest({ kind: "ok", shopName: data.shopName });
      } else {
        setTest({ kind: "error", message: data.error || "Connection failed" });
      }
    } catch {
      setTest({ kind: "error", message: "Network error" });
    }
  }, [shopDomain, accessToken, tokenMasked]);

  const onSave = useCallback(async () => {
    const domain = shopDomain.trim();
    if (!domain) {
      setError("Shop domain is required");
      return;
    }
    if (tokenMasked || accessToken.length === 0) {
      setError("Enter an access token (click Replace to change an existing one)");
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await saveShopifyConnection({ shopDomain: domain, accessToken });
      setConnected(true);
      setTokenMasked(true);
      setAccessToken("••••••••••••••••");
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }, [shopDomain, accessToken, tokenMasked]);

  return (
    <Card
      title="Shopify connection"
      subtitle="Connect your store with a custom-app Admin API token. Read-only; used to verify the connection."
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-zinc-100 dark:bg-surface-2 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
          Shopify only
        </span>
        <StatusPill connected={connected} />
        {test.kind === "testing" ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 dark:bg-surface-2 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
            <span
              aria-hidden
              className="inline-block h-2 w-2 animate-pulse rounded-full bg-zinc-400 dark:bg-zinc-600"
            />
            Testing…
          </span>
        ) : test.kind === "ok" ? (
          <span className="rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            ✓ {test.shopName}
          </span>
        ) : test.kind === "error" ? (
          <span className="rounded-full bg-red-50 dark:bg-red-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-red-700 dark:text-red-300">
            ✗ {test.message}
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Shop domain
          </span>
          <input
            type="text"
            value={shopDomain}
            onChange={(e) => setShopDomain(e.target.value)}
            placeholder="monjolika.myshopify.com"
            className="mt-1 w-full rounded-lg border border-zinc-200 dark:border-border bg-white dark:bg-surface px-3 py-2 font-mono text-[12px] text-zinc-900 dark:text-zinc-100 outline-none focus:border-zinc-900 dark:focus:border-zinc-400"
          />
        </label>

        <label className="block">
          <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Admin API access token
          </span>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="password"
              value={accessToken}
              onChange={(e) => {
                setAccessToken(e.target.value);
                if (tokenMasked) setTokenMasked(false);
              }}
              placeholder="shpat_…"
              readOnly={tokenMasked}
              className="flex-1 rounded-lg border border-zinc-200 dark:border-border bg-white dark:bg-surface px-3 py-2 font-mono text-[12px] text-zinc-900 dark:text-zinc-100 outline-none focus:border-zinc-900 dark:focus:border-zinc-400"
            />
            {tokenMasked ? (
              <button
                type="button"
                onClick={onReplaceToken}
                className="rounded-md border border-zinc-200 dark:border-border px-2 py-1 text-[11px] text-zinc-700 dark:text-zinc-300 transition hover:border-zinc-900 dark:hover:border-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
              >
                Replace
              </button>
            ) : null}
          </div>
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-end gap-3 border-t border-zinc-100 dark:border-border pt-3">
        {error ? (
          <span className="text-[12px] text-red-600 dark:text-red-400">{error}</span>
        ) : saved ? (
          <span className="font-mono text-[11px] text-emerald-600 dark:text-emerald-400">Saved ✓</span>
        ) : dirty ? (
          <span className="font-mono text-[11px] text-amber-600 dark:text-amber-400">Unsaved</span>
        ) : null}
        <button
          type="button"
          onClick={runTest}
          disabled={test.kind === "testing" || saving}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-border bg-white dark:bg-surface px-3 py-1.5 text-[12px] font-medium text-zinc-700 dark:text-zinc-300 transition hover:border-zinc-900 dark:hover:border-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {test.kind === "testing" ? "Testing…" : "Test connection"}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="rounded-lg bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-zinc-800 dark:hover:bg-zinc-200 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <p className="mt-3 text-[11px] text-zinc-500 dark:text-zinc-400">
        Create a custom app in your Shopify admin (Settings → Apps → Develop apps),
        grant <span className="font-mono">read_products</span> scope, and paste its
        Admin API access token. Stored server-side only. Read-only, never writes to
        your store.
      </p>
    </Card>
  );
}
